import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFlexFlagIsOn } from '@indeed/flex-feature-flags'

import { fetchConfiguredUplift } from './actions'
import { scopeKeyOf } from './helpers'
import type { ConfiguredUpliftEntity, FetchConfiguredUpliftPayload } from './types'

type ConfiguredUpliftState = {
  configuredUplift?: { entityDetail?: Record<string, ConfiguredUpliftEntity> }
}

/** The only card the amount belongs on. Kept here so no call site has to get it right. */
const RATES_GROUP = 'rates'

/**
 * The same flag that gates the Inspector screen these amounts are set on, and that the engine
 * checks before honouring them. Read here rather than inferred from an empty response, so the
 * screen asks nothing at all where the feature is not live.
 */
const MANAGED_UPLIFT_FEATURE = 'stsmpd_2176_managed_uplift'

/**
 * The configured uplift for whichever scope the page is showing.
 *
 * Undefined unless the feature is live, this is the rates card, and the page supplied a client.
 * An absent value is ordinary rather than a failure, and the caller renders nothing: a zero would
 * read as "no uplift here", which is a different statement.
 */
export const useConfiguredUplift = (scope?: {
  group?: string
  employerId?: number | null
  workLocationId?: number | null
}) => {
  const featureOn = useFlexFlagIsOn(MANAGED_UPLIFT_FEATURE)
  const enabled = featureOn && scope?.group === RATES_GROUP
  const employerId = scope?.employerId
  const workLocationId = scope?.workLocationId

  const payload: FetchConfiguredUpliftPayload | null = useMemo(
    () =>
      enabled && employerId
        ? {
            employer_id: employerId,
            ...(workLocationId ? { work_location_id: workLocationId } : {}),
          }
        : null,
    [enabled, employerId, workLocationId],
  )

  const dispatch = useDispatch()

  useEffect(() => {
    if (!payload) return
    dispatch(fetchConfiguredUplift({ options: payload }))
  }, [dispatch, payload])

  return useSelector((state: ConfiguredUpliftState) =>
    payload ? state.configuredUplift?.entityDetail?.[scopeKeyOf(payload)] : undefined,
  )
}
